/**
 * PaymentSettingsStepConfig
 *
 * Studio-builder-only step config section. Lets merchants translate the
 * payment selector + express checkout strings per BCP-47 locale.
 *
 * Data shape (RuntimeStepConfig.paymentSettings):
 *   {
 *     methods: { [type]: { translations: { [locale]: { label, description, promptText, instructions } } } }
 *     translations: { [locale]: { title, dividerText, ..., cardNumberError, ... } }
 *   }
 *
 * Studio's PaymentMethodSelector + ExpressCheckout read this overlay at
 * render time, falling back to paymentSetupConfig values then to component
 * defaults when an entry isn't translated.
 */

import { ChevronDown, Globe, Languages, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

import type { RuntimeStepConfig } from '../types'

type PaymentSettings = NonNullable<RuntimeStepConfig['paymentSettings']>
type ChromeTranslation = NonNullable<NonNullable<PaymentSettings['translations']>[string]>
type MethodTranslation = NonNullable<NonNullable<NonNullable<PaymentSettings['methods']>[string]['translations']>[string]>

interface PaymentSettingsStepConfigProps {
  value: RuntimeStepConfig['paymentSettings']
  onChange: (next: RuntimeStepConfig['paymentSettings']) => void
}

// Mirrors the catalog the studio renderer recognizes. Adding a method
// here without wiring it on the studio side means the inputs persist
// but no UI consumes them.
const METHOD_TYPES: Array<{ type: string; label: string }> = [
  { type: 'credit-card', label: 'Credit Card' },
  { type: 'apple-pay', label: 'Apple Pay' },
  { type: 'google-pay', label: 'Google Pay' },
  { type: 'paypal', label: 'PayPal' },
  { type: 'oceanpayment', label: 'Ocean Payment' },
  { type: 'ideal', label: 'iDEAL' },
  { type: 'bancontact', label: 'Bancontact' },
  { type: 'twint', label: 'TWINT' },
  { type: 'blik', label: 'BLIK' },
  { type: 'skrill', label: 'Skrill' },
  { type: 'klarna', label: 'Klarna' },
  { type: 'afterpay', label: 'Afterpay' },
  { type: 'affirm', label: 'Affirm' },
  { type: 'zelle', label: 'Zelle' },
  { type: 'custom_payment', label: 'Custom Payment' },
  { type: 'crypto', label: 'Card to crypto' },
  { type: 'crypto-onchain', label: 'Crypto to crypto' },
  { type: 'hipay', label: 'HiPay' },
  { type: 'whop', label: 'Whop' },
]

const CHROME_FIELDS: Array<{ key: keyof ChromeTranslation; label: string; multiline?: boolean }> = [
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'dividerText', label: 'Divider Text' },
  { key: 'redirectText', label: 'Redirect Text', multiline: true },
  { key: 'processingLabel', label: 'Processing Label (Express)' },
  { key: 'cardNumberLabel', label: 'Card Number Label' },
  { key: 'expiryLabel', label: 'Expiry Label' },
  { key: 'cvcLabel', label: 'CVC Label' },
  { key: 'cardNumberError', label: 'Card Number Error' },
  { key: 'expiryError', label: 'Expiry Error' },
  { key: 'expiryFutureError', label: 'Expiry Future Error' },
  { key: 'cvcError', label: 'CVC Error' },
]

const METHOD_FIELDS: Array<{ key: keyof MethodTranslation; label: string; multiline?: boolean; appliesToType?: (type: string) => boolean }> = [
  { key: 'label', label: 'Label' },
  { key: 'description', label: 'Description' },
  { key: 'promptText', label: 'Prompt Text', multiline: true, appliesToType: (t) => t !== 'credit-card' && t !== 'oceanpayment' },
  { key: 'instructions', label: 'Instructions', multiline: true, appliesToType: (t) => t === 'zelle' || t === 'custom_payment' },
]

export function PaymentSettingsStepConfig({ value, onChange }: PaymentSettingsStepConfigProps) {
  const { t } = useTranslation()
  const settings: PaymentSettings = value ?? {}

  // Locales surfaced to the merchant — union of every locale that has any
  // translation under either chrome or per-method blocks.
  const knownLocales = useMemo(() => {
    const set = new Set<string>()
    for (const loc of Object.keys(settings.translations ?? {})) set.add(loc)
    for (const m of Object.values(settings.methods ?? {})) {
      for (const loc of Object.keys(m.translations ?? {})) set.add(loc)
    }
    return Array.from(set).sort()
  }, [settings])

  const [activeLocale, setActiveLocale] = useState<string>(knownLocales[0] ?? '')
  const [newLocaleInput, setNewLocaleInput] = useState('')

  const update = (next: PaymentSettings) => {
    // Normalize: strip empty objects so the saved config stays compact.
    const cleaned: PaymentSettings = {}
    if (next.translations && Object.keys(next.translations).length > 0) {
      cleaned.translations = next.translations
    }
    if (next.methods && Object.keys(next.methods).length > 0) {
      cleaned.methods = next.methods
    }
    onChange(Object.keys(cleaned).length > 0 ? cleaned : undefined)
  }

  const addLocale = (rawLocale: string) => {
    const locale = rawLocale.trim()
    if (!locale) return
    if (knownLocales.includes(locale)) {
      setActiveLocale(locale)
      setNewLocaleInput('')
      return
    }
    update({
      ...settings,
      translations: {
        ...(settings.translations ?? {}),
        [locale]: {},
      },
    })
    setActiveLocale(locale)
    setNewLocaleInput('')
  }

  const removeLocale = (locale: string) => {
    const nextTranslations = { ...(settings.translations ?? {}) }
    delete nextTranslations[locale]
    const nextMethods = { ...(settings.methods ?? {}) }
    for (const [type, m] of Object.entries(nextMethods)) {
      if (!m.translations) continue
      const tCopy = { ...m.translations }
      delete tCopy[locale]
      if (Object.keys(tCopy).length === 0) {
        const nextEntry = { ...m }
        delete nextEntry.translations
        if (Object.keys(nextEntry).length === 0) {
          delete nextMethods[type]
        } else {
          nextMethods[type] = nextEntry
        }
      } else {
        nextMethods[type] = { ...m, translations: tCopy }
      }
    }
    update({ ...settings, translations: nextTranslations, methods: nextMethods })
    if (activeLocale === locale) {
      const remaining = knownLocales.filter((l) => l !== locale)
      setActiveLocale(remaining[0] ?? '')
    }
  }

  const setChromeField = (locale: string, key: keyof ChromeTranslation, val: string) => {
    const trimmed = val.trim() === '' ? undefined : val
    const localeMap = { ...(settings.translations ?? {}) }
    const current = { ...(localeMap[locale] ?? {}) }
    if (trimmed === undefined) {
      delete current[key]
    } else {
      current[key] = trimmed
    }
    if (Object.keys(current).length === 0) {
      delete localeMap[locale]
    } else {
      localeMap[locale] = current
    }
    update({ ...settings, translations: localeMap })
  }

  const setMethodField = (
    type: string,
    locale: string,
    key: keyof MethodTranslation,
    val: string,
  ) => {
    const trimmed = val.trim() === '' ? undefined : val
    const methods = { ...(settings.methods ?? {}) }
    const entry = { ...(methods[type] ?? {}) }
    const translations = { ...(entry.translations ?? {}) }
    const current = { ...(translations[locale] ?? {}) }
    if (trimmed === undefined) {
      delete current[key]
    } else {
      current[key] = trimmed
    }
    if (Object.keys(current).length === 0) {
      delete translations[locale]
    } else {
      translations[locale] = current
    }
    if (Object.keys(translations).length === 0) {
      delete entry.translations
    } else {
      entry.translations = translations
    }
    if (Object.keys(entry).length === 0) {
      delete methods[type]
    } else {
      methods[type] = entry
    }
    update({ ...settings, methods })
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        {t('stepEditor.paymentSettings.description')}
      </p>

      {/* Locale tabs */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          <Languages className="mr-1.5 inline h-3.5 w-3.5 text-slate-400" />
          {t('stepEditor.paymentSettings.locales')}
        </Label>

        {knownLocales.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {knownLocales.map((locale) => (
              <button
                key={locale}
                type="button"
                onClick={() => setActiveLocale(locale)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                  activeLocale === locale
                    ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200',
                )}
              >
                <Globe className="h-3 w-3" />
                {locale}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeLocale(locale)
                  }}
                  className="ml-1 text-slate-400 hover:text-red-500"
                  aria-label={`Remove ${locale}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Input
            value={newLocaleInput}
            onChange={(e) => setNewLocaleInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addLocale(newLocaleInput)
              }
            }}
            placeholder={t('stepEditor.paymentSettings.localePlaceholder')}
            className="h-8 text-xs"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addLocale(newLocaleInput)}
            disabled={!newLocaleInput.trim()}
            className="h-8 shrink-0"
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t('stepEditor.paymentSettings.addLocale')}
          </Button>
        </div>
      </div>

      {!activeLocale && knownLocales.length === 0 && (
        <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900">
          {t('stepEditor.paymentSettings.empty')}
        </div>
      )}

      {activeLocale && (
        <div className="space-y-3">
          {/* Chrome translations */}
          <Collapsible defaultOpen>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                {t('stepEditor.paymentSettings.chrome', { locale: activeLocale })}
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-2 rounded-md border border-slate-200 bg-slate-50/50 p-3 dark:border-slate-700 dark:bg-slate-900/30">
              {CHROME_FIELDS.map((field) => (
                <ChromeFieldInput
                  key={field.key}
                  label={field.label}
                  value={settings.translations?.[activeLocale]?.[field.key] ?? ''}
                  onChange={(val) => setChromeField(activeLocale, field.key, val)}
                  multiline={field.multiline}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>

          {/* Per-method translations */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
              {t('stepEditor.paymentSettings.methods', { locale: activeLocale })}
            </Label>
            <div className="space-y-1.5">
              {METHOD_TYPES.map((m) => (
                <MethodTranslationBlock
                  key={m.type}
                  type={m.type}
                  label={m.label}
                  values={settings.methods?.[m.type]?.translations?.[activeLocale] ?? {}}
                  onChange={(key, val) => setMethodField(m.type, activeLocale, key, val)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ChromeFieldInput({
  label,
  value,
  onChange,
  multiline,
}: {
  label: string
  value: string
  onChange: (val: string) => void
  multiline?: boolean
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
        {label}
      </Label>
      {multiline ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className="text-xs"
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 text-xs"
        />
      )}
    </div>
  )
}

function MethodTranslationBlock({
  type,
  label,
  values,
  onChange,
}: {
  type: string
  label: string
  values: MethodTranslation
  onChange: (key: keyof MethodTranslation, val: string) => void
}) {
  const filledCount = (Object.values(values) as Array<string | undefined>).filter(
    (v) => typeof v === 'string' && v.length > 0,
  ).length
  const fields = METHOD_FIELDS.filter((f) => !f.appliesToType || f.appliesToType(type))

  return (
    <Collapsible>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900"
        >
          <span className="font-medium text-slate-700 dark:text-slate-200">
            {label}
          </span>
          <span className="flex items-center gap-1.5">
            {filledCount > 0 && (
              <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-600">
                {filledCount} translated
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-2 rounded-md border border-slate-200 bg-slate-50/50 p-3 dark:border-slate-700 dark:bg-slate-900/30">
        {fields.map((field) => (
          <ChromeFieldInput
            key={field.key}
            label={field.label}
            value={values[field.key] ?? ''}
            onChange={(val) => onChange(field.key, val)}
            multiline={field.multiline}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}
